export const AUDIO_SETTINGS_STORAGE_KEY = "breakroom-audio-v4";
const PREVIOUS_AUDIO_SETTINGS_STORAGE_KEY = "breakroom-audio-v3";

export type AudioBus = "master" | "music" | "sfx" | "voice";
export type MixBus = Exclude<AudioBus, "master">;

export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
  voice: number;
  menuTheme: boolean;
}

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  master: 0.15,
  music: 1,
  sfx: 1,
  voice: 1,
  menuTheme: true
};

export const AUDIO_BUS_LABELS: Record<AudioBus, string> = {
  master: "Master",
  music: "Music",
  sfx: "Sound effects",
  voice: "Voice"
};

type Listener = (settings: AudioSettings) => void;

const listeners = new Set<Listener>();
let settings = loadAudioSettings();

export function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

export function parseAudioSettings(raw: unknown): AudioSettings {
  const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    master: clampVolume(typeof source.master === "number" ? source.master : DEFAULT_AUDIO_SETTINGS.master),
    music: clampVolume(typeof source.music === "number" ? source.music : DEFAULT_AUDIO_SETTINGS.music),
    sfx: clampVolume(typeof source.sfx === "number" ? source.sfx : DEFAULT_AUDIO_SETTINGS.sfx),
    voice: clampVolume(typeof source.voice === "number" ? source.voice : DEFAULT_AUDIO_SETTINGS.voice),
    menuTheme:
      typeof source.menuTheme === "boolean" ? source.menuTheme : DEFAULT_AUDIO_SETTINGS.menuTheme
  };
}

export function mixBusVolume(current: AudioSettings, bus: MixBus, base = 1): number {
  return clampVolume(base) * clampVolume(current.master) * clampVolume(current[bus]);
}

export function getAudioSettings(): AudioSettings {
  return { ...settings };
}

export function mixedVolume(bus: MixBus, base = 1): number {
  return mixBusVolume(settings, bus, base);
}

export function setAudioSettings(patch: Partial<AudioSettings>): AudioSettings {
  settings = parseAudioSettings({ ...settings, ...patch });
  persistAudioSettings(settings);
  for (const listener of listeners) listener(getAudioSettings());
  return getAudioSettings();
}

export function setAudioBus(bus: AudioBus, value: number): AudioSettings {
  return setAudioSettings({ [bus]: clampVolume(value) });
}

export function subscribeAudioSettings(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function resetAudioSettings(): AudioSettings {
  return setAudioSettings({ ...DEFAULT_AUDIO_SETTINGS });
}

function loadAudioSettings(): AudioSettings {
  try {
    const stored = globalThis.localStorage?.getItem(AUDIO_SETTINGS_STORAGE_KEY);
    if (stored) return parseAudioSettings(JSON.parse(stored));
    const previous = globalThis.localStorage?.getItem(PREVIOUS_AUDIO_SETTINGS_STORAGE_KEY);
    if (!previous) return { ...DEFAULT_AUDIO_SETTINGS };
    const migrated = parseAudioSettings({
      ...JSON.parse(previous),
      master: DEFAULT_AUDIO_SETTINGS.master
    });
    persistAudioSettings(migrated);
    return migrated;
  } catch {
    return { ...DEFAULT_AUDIO_SETTINGS };
  }
}

function persistAudioSettings(next: AudioSettings): void {
  try {
    globalThis.localStorage?.setItem(AUDIO_SETTINGS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode and missing storage should not block the mixer.
  }
}
